import React, { Fragment, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Combobox, Dialog, Transition } from '@headlessui/react'
import { FiSearch, FiMail, FiLogOut, FiCornerDownLeft } from 'react-icons/fi'
import { useAuth } from 'context/auth'
import EmptyState from 'components/empty-state'
import { SUPPORT_EMAIL } from './nav-config'

const buildItems = (entries) => {
  const items = []
  entries.forEach((entry) => {
    items.push({ id: `nav-${entry.key}`, label: entry.label, hint: 'Go to', icon: entry.icon, to: entry.to })
    entry.tabs?.forEach((tab) => {
      items.push({
        id: `tab-${entry.key}-${tab.to}-${tab.label}`,
        label: tab.label,
        hint: entry.label,
        icon: entry.icon,
        to: tab.to,
      })
    })
  })
  items.push({ id: 'email-support', label: 'Email support', hint: 'Help', icon: FiMail, action: 'email' })
  items.push({ id: 'logout', label: 'Log out', hint: 'Account', icon: FiLogOut, action: 'logout' })
  return items
}

function CommandPalette({ open, onClose, entries }) {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [query, setQuery] = useState('')

  const items = useMemo(() => buildItems(entries), [entries])
  const needle = query.trim().toLowerCase()
  const filtered = needle
    ? items.filter((item) => `${item.hint} ${item.label}`.toLowerCase().includes(needle))
    : items

  const handleSelect = (item) => {
    if (!item) return
    onClose()
    if (item.action === 'email') window.location.href = `mailto:${SUPPORT_EMAIL}`
    else if (item.action === 'logout') logout()
    else navigate(item.to)
  }

  return (
    <Transition show={open} as={Fragment} afterLeave={() => setQuery('')}>
      <Dialog as='div' className='tw-relative tw-z-[110]' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='tw-transition-opacity tw-duration-150'
          enterFrom='tw-opacity-0'
          enterTo='tw-opacity-100'
          leave='tw-transition-opacity tw-duration-100'
          leaveFrom='tw-opacity-100'
          leaveTo='tw-opacity-0'
        >
          <div aria-hidden='true' className='tw-fixed tw-inset-0 tw-bg-ink/40' />
        </Transition.Child>

        <div className='tw-fixed tw-inset-0 tw-overflow-y-auto tw-px-4 tw-pt-[12vh]'>
          <Transition.Child
            as={Fragment}
            enter='tw-transition tw-duration-150 tw-ease-out'
            enterFrom='tw-opacity-0 tw-scale-95'
            enterTo='tw-opacity-100 tw-scale-100'
            leave='tw-transition tw-duration-100 tw-ease-in'
            leaveFrom='tw-opacity-100 tw-scale-100'
            leaveTo='tw-opacity-0 tw-scale-95'
          >
            <Dialog.Panel className='tw-mx-auto tw-w-full tw-max-w-xl tw-overflow-hidden tw-rounded-2xl tw-bg-white tw-font-sans tw-shadow-pop'>
              <Dialog.Title className='tw-sr-only'>Search</Dialog.Title>
              <Combobox onChange={handleSelect}>
                <div className='tw-flex tw-items-center tw-gap-3 tw-border-b tw-border-line tw-px-4'>
                  <FiSearch aria-hidden='true' className='tw-h-5 tw-w-5 tw-shrink-0 tw-text-inkFaint' />
                  <Combobox.Input
                    autoFocus
                    aria-label='Search pages and actions'
                    placeholder='Search pages and actions'
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      // Combobox consumes the first Esc to clear its own state; close the palette in one press.
                      if (event.key === 'Escape') {
                        event.preventDefault()
                        event.stopPropagation()
                        onClose()
                      }
                    }}
                    className='tw-h-12 tw-w-full tw-border-0 tw-bg-transparent tw-text-[14px] tw-text-ink placeholder:tw-text-inkFaint focus:tw-outline-none focus:tw-ring-0'
                  />
                </div>
                {filtered.length > 0 ? (
                  <Combobox.Options static className='tw-max-h-80 tw-overflow-y-auto tw-p-2'>
                    {filtered.map((item) => {
                      const Icon = item.icon
                      return (
                        <Combobox.Option
                          key={item.id}
                          value={item}
                          className={({ active }) =>
                            `tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[10px] tw-px-3 tw-py-2 tw-text-[14px] ${
                              active ? 'tw-bg-emberSoft tw-text-ink' : 'tw-text-ink'
                            }`
                          }
                        >
                          {({ active }) => (
                            <>
                              <Icon
                                aria-hidden='true'
                                className={`tw-h-4 tw-w-4 tw-shrink-0 ${active ? 'tw-text-ember' : 'tw-text-inkSoft'}`}
                              />
                              <span className='tw-min-w-0 tw-flex-1 tw-truncate'>{item.label}</span>
                              <span className='tw-shrink-0 tw-text-[12px] tw-text-inkFaint'>{item.hint}</span>
                              {active && (
                                <FiCornerDownLeft aria-hidden='true' className='tw-h-4 tw-w-4 tw-shrink-0 tw-text-inkFaint' />
                              )}
                            </>
                          )}
                        </Combobox.Option>
                      )
                    })}
                  </Combobox.Options>
                ) : (
                  <EmptyState icon={FiSearch} message='No matches' />
                )}
              </Combobox>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}

export default CommandPalette
